import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface CompanyFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: {
    id?: string;
    name: string;
    website?: string;
    industry?: string;
    description?: string;
  };
  isEdit?: boolean;
}

export default function CompanyForm({ onSuccess, onCancel, initialData, isEdit }: CompanyFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    website: initialData?.website || '',
    industry: initialData?.industry || '',
    description: initialData?.description || ''
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const validateWebsite = (url: string): boolean => {
    if (!url) return true; // Allow empty website
    // Remove any protocol if present
    const urlWithoutProtocol = url.replace(/^(https?:\/\/)?(www\.)?/, '');
    // Check for domain.tld format
    return /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}/.test(urlWithoutProtocol);
  };

  const normalizeWebsite = (url: string): string => {
    if (!url) return '';
    // Add https if no protocol is present
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }

    if (!validateWebsite(formData.website)) {
      setError('Invalid website URL format. Please enter a valid domain (e.g., example.com)');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEdit && initialData?.id) {
        // Update existing company
        const { error: saveError } = await supabase
          .from('nrm_companies')
          .update({
            name: formData.name.trim(),
            website: formData.website ? normalizeWebsite(formData.website.trim()) : null,
            industry: formData.industry.trim() || null,
            description: formData.description.trim() || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', initialData.id);

        if (saveError) throw saveError;
      } else {
        // Create new company
        const { error: saveError } = await supabase
          .from('nrm_companies')
          .insert([{
            name: formData.name.trim(),
            website: formData.website ? normalizeWebsite(formData.website.trim()) : null,
            industry: formData.industry.trim() || null,
            description: formData.description.trim() || null,
            user_id: user.id
          }]);

        if (saveError) throw saveError;
      }

      onSuccess();
    } catch (err) {
      setError('Failed to save company. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="input w-full"
          placeholder="Enter company name"
          required
        />
      </div>

      <div>
        <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-1">
          Website
        </label>
        <input
          type="text"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          className="input w-full"
          placeholder="e.g., example.com"
        />
      </div>

      <div>
        <label htmlFor="industry" className="block text-sm font-medium text-gray-300 mb-1">
          Industry
        </label>
        <input
          type="text"
          id="industry"
          name="industry"
          value={formData.industry}
          onChange={handleChange}
          className="input w-full"
          placeholder="Enter industry"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="input w-full"
          rows={3}
          placeholder="Enter company description"
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEdit ? 'Update Company' : 'Save Company'}
        </button>
      </div>
    </form>
  );
}